import { uniq } from 'ramda';
import { nextPipe } from './pipeLadders';
import {
  AssignerProgress,
  AssignerRun,
  Assignment,
  FailureReason,
  LadderPipe,
  Ladders,
  OwnerInfo,
  ReachableEvaluation,
  SegmentLoad,
  SpoolClass,
  StopeEvaluation,
  StopeOutcome
} from './types';

export const MAX_PASSES = 20;
export const MAX_ITERATIONS = 50;

export interface RunAutoPipeAssignerInput {
  stopeIds: string[];
  // The owner spools on each stope's route.
  ownersByStope: Record<string, string[]>;
  owners: Record<string, OwnerInfo>;
  pipes: Record<string, LadderPipe>;
  ladders: Ladders;
  evaluate: (stopeId: string, assignment: Assignment) => StopeEvaluation;
  // The pipes the first pass starts from; a spool absent from it starts on its own pipe.
  initialAssignment?: Assignment;
  signal?: AbortSignal;
  onProgress?: (progress: AssignerProgress) => void;
  yieldToUi?: () => Promise<void>;
  maxPasses?: number;
  maxIterations?: number;
}

interface Failure {
  reason: FailureReason;
  blockingSpoolIds?: string[];
  blockingClass?: SpoolClass;
}

interface PassState {
  assignment: Assignment;
  outcomes: Record<string, StopeOutcome>;
}

interface FixedStope {
  assignment: Assignment;
  outcome: StopeOutcome;
}

type UpgradePlan = { upgrades: Array<[string, string]> } | { failure: Failure };

export const yieldToBrowser = () => new Promise<void>((resolve) => { setTimeout(resolve, 0); });

const currentRating = ({ owners, pipes }: RunAutoPipeAssignerInput, assignment: Assignment, ownerId: string): number => (
  pipes[assignment.get(ownerId) ?? owners[ownerId].livePipeId]?.pressureRating ?? -Infinity
);

// Every overpressured spool steps up one rung of its own class; the stope fails if any of them cannot.
const planUpgrades = (input: RunAutoPipeAssignerInput, assignment: Assignment, overpressured: SegmentLoad[]): UpgradePlan => {
  if (overpressured.some((load) => !load.ownerSpoolId)) return { failure: { reason: 'dischargePipe' } };

  const ownerIds = uniq(overpressured.map((load) => load.ownerSpoolId as string));
  const steps = ownerIds.map((ownerId) => {
    const { spoolClass } = input.owners[ownerId];
    return { ownerId, spoolClass, next: nextPipe(input.ladders[spoolClass], currentRating(input, assignment, ownerId)) };
  });
  const blocked = steps.find((step) => !step.next);
  if (blocked) return { failure: { reason: 'noHigherPipe', blockingSpoolIds: [blocked.ownerId], blockingClass: blocked.spoolClass } };

  return { upgrades: steps.map(({ ownerId, next }) => [ownerId, (next as LadderPipe).pipeId]) };
};

const withUpgrades = (assignment: Assignment, upgrades: Array<[string, string]>): Assignment => new Map([...Array.from(assignment.entries()), ...upgrades]);

const sameAssignment = (a: Assignment, b: Assignment): boolean => a.size === b.size
  && Array.from(a.entries()).every(([ownerId, pipeId]) => b.get(ownerId) === pipeId);

// A stope that cannot be fixed leaves the pass's assignment as it found it.
const fixStope = (input: RunAutoPipeAssignerInput, stopeId: string, start: Assignment): Promise<FixedStope | undefined> => {
  const { evaluate, signal, yieldToUi = yieldToBrowser } = input;
  const maxIterations = input.maxIterations ?? MAX_ITERATIONS;

  const attempt = async (
    assignment: Assignment,
    iteration: number,
    first: ReachableEvaluation | undefined,
    stepped: string[],
  ): Promise<FixedStope | undefined> => {
    await yieldToUi();
    if (signal?.aborted) return undefined;

    const evaluation = evaluate(stopeId, assignment);
    const evaluatedAtStart = iteration === 1 && evaluation.reachable ? evaluation : first;
    const fail = (failure: Failure): FixedStope => ({
      assignment: start,
      outcome: { status: 'failed', ...failure, evaluation: evaluatedAtStart },
    });

    // A narrower bore can leave no mix that reaches the stope; then the upgrade, not the pump, is what fails it.
    if (!evaluation.reachable) {
      return fail(iteration === 1 ? { reason: evaluation.reason } : { reason: 'upgradeUnreachable', blockingSpoolIds: stepped });
    }
    if (evaluation.overpressured.length === 0) return { assignment, outcome: { status: 'passed', evaluation } };
    if (iteration >= maxIterations) return fail({ reason: 'didNotConverge' });

    const plan = planUpgrades(input, assignment, evaluation.overpressured);
    if ('failure' in plan) return fail(plan.failure);
    return attempt(withUpgrades(assignment, plan.upgrades), iteration + 1, evaluatedAtStart, plan.upgrades.map(([ownerId]) => ownerId));
  };

  return attempt(start, 1, undefined, []);
};

// A stope that passed in one pass and failed in a later one still holds its earlier upgrades. Only pipes on a stope's
// own route move its pressures, so dropping every upgrade no passing stope's route crosses changes no passing verdict.
const keptForPassedStopes = (input: RunAutoPipeAssignerInput, { assignment, outcomes }: PassState): Assignment => {
  const needed = new Set(input.stopeIds
    .filter((stopeId) => outcomes[stopeId]?.status === 'passed')
    .flatMap((stopeId) => input.ownersByStope[stopeId] ?? []));
  return new Map(Array.from(assignment.entries()).filter(([ownerId]) => needed.has(ownerId)));
};

const runPass = (input: RunAutoPipeAssignerInput, pass: number, start: Assignment): Promise<PassState | undefined> => input.stopeIds.reduce(
  (previous: Promise<PassState | undefined>, stopeId, stopeIndex) => previous.then(async (state) => {
    if (!state) return undefined;
    input.onProgress?.({
      stage: 'solving', stopeIndex, stopeCount: input.stopeIds.length, pass,
    });
    const fixed = await fixStope(input, stopeId, state.assignment);
    return fixed && { assignment: fixed.assignment, outcomes: { ...state.outcomes, [stopeId]: fixed.outcome } };
  }),
  Promise.resolve({ assignment: start, outcomes: {} }),
);

const notConverged = (outcomes: Record<string, StopeOutcome>): Record<string, StopeOutcome> => Object.keys(outcomes).reduce(
  (acc, stopeId) => ({ ...acc, [stopeId]: { status: 'failed', reason: 'didNotConverge' } }),
  {},
);

/**
 * Steps overpressured spools up their class's pipe ladder, from the initial assignment, until every stope passes or
 * provably cannot. Within a run of passes upgrades only go up, and a pass that changes nothing has evaluated every stope against its assignment, so the
 * last pass is the verification pass. Changes only failed stopes hold, upgrades or starting pipes, are then dropped and the
 * result re-verified.
 * Resolves undefined when cancelled.
 */
export const runAutoPipeAssigner = (input: RunAutoPipeAssignerInput): Promise<AssignerRun | undefined> => {
  const maxPasses = input.maxPasses ?? MAX_PASSES;

  const loop = async (pass: number, start: Assignment): Promise<AssignerRun | undefined> => {
    const state = await runPass(input, pass, start);
    if (!state) return undefined;
    const settled = sameAssignment(start, state.assignment);
    const next = settled ? keptForPassedStopes(input, state) : state.assignment;
    if (settled && sameAssignment(next, state.assignment)) return { ...state, converged: true };
    if (pass >= maxPasses) return { assignment: state.assignment, outcomes: notConverged(state.outcomes), converged: false };
    return loop(pass + 1, next);
  };

  return loop(1, input.initialAssignment ?? new Map());
};
